/**
 * Groups an array of objects by a specified key.
 *
 * @param items - The array of objects to group.
 * @param key - The key to group the objects by.
 * @returns A Map where the keys are the unique values of the specified key and the values are arrays of objects that
 * share that key value.
 *
 *  @example
 * ```typescript
 *  const items = [
 *    { id: 1, category: 'A' },
 *    { id: 2, category: 'B' },
 *    { id: 3, category: 'A' },
 *    { id: 4, category: 'C' },
 *  ];
 *  const grouped = groupBy(items, 'category');
 *  console.log(grouped);
 *  // Output: Map {
 *  //   'A' => [{ id: 1, category: 'A' }, { id: 3, category: 'A' }],
 *  //   'B' => [{ id: 2, category: 'B' }],
 *  //   'C' => [{ id: 4, category: 'C' }]
 *  // }
 * ```
 */
export const groupBy = <TK extends keyof T, T = unknown>(items: T[], key: TK): Map<T[TK], T[]> => {
  const map = new Map<T[TK], T[]>();

  for (const item of items) {
    const itemKey = item[key];

    if (!map.has(itemKey)) {
      const grouped = items.filter((i) => {
        return i[key] === item[key];
      });

      map.set(itemKey, grouped);
    }
  }

  return map;
};
